import { profileHash } from "PFCore/helpers/profile_hash";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Profile } from "PFTypes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { slugify } from "underscore.string";

import { getAvatarDataUrl } from "./get_image_data_url";
import { getLocalExportBuildPptx } from "./helpers/custom_pptx_template";
import profilesToTable from "./helpers/profiles_to_table";
import localExportBuildDocx from "./local_export_build_docx";
import localExportBuildPdf from "./local_export_build_pdf";
import useDownloadCsv from "./use_download_csv";

const useDownloadProfiles = (onDownload, config, exportableProfiles, exportableProfile, logo) => {
  const { customTypes } = useCustomTypes();

  const { t } = useTranslation("profiles", { keyPrefix: "export" });
  const { formatDate } = useDateFormatter();

  const serializedProfileRef = useRef<Profile | null>(null);
  const [avatar, setAvatar] = useState(null);
  const { order } = config;
  const { formatAndDownload } = useDownloadCsv();
  const { formatDateTime } = useDateFormatter();

  serializedProfileRef.current = exportableProfile?.serialize({ avatar });
  const serializedProfile = serializedProfileRef.current;

  useEffect(() => {
    if (!exportableProfile) {
      return;
    }

    if (config.showAvatar) {
      getAvatarDataUrl(exportableProfile).then((avatar) => {
        setAvatar(avatar);
      });
    }
  }, [exportableProfile, customTypes, config.showAvatar]);

  const batchDownload = (promises, suffix, saveAs, JSZip) => {
    if (promises.length === 1) {
      promises[0].then(({ data, filename }) => saveAs(data, `${filename}${suffix}`));
    } else {
      const zip = new JSZip();
      Promise.all(promises).then((items) => {
        items.forEach(({ data, filename }) => zip.file(`${filename}${suffix}`, data));
        zip.generateAsync({ type: "blob" }).then((blob) => saveAs(blob, "profiles.zip"));
      });
    }
  };

  const getProfilesWithAvatars = (profiles, process) =>
    profiles.map(
      (profile) =>
        new Promise((profileResolve) => {
          const serializedProfile = profile.serialize();
          getAvatarDataUrl(serializedProfile).then((avatar) => {
            serializedProfile.avatar = config.showAvatar ? avatar : null;
            serializedProfile.logo = logo;
            const { first_name, last_name, id } = serializedProfile;
            const filename = slugify(
              config.isBlindModeOn ? `Candidate ${profileHash(id)}` : `${first_name}_${last_name || ""}_${id}`
            ).replace(/-+/g, "_");
            process(serializedProfile, filename, profileResolve);
          });
        })
    );

  const buildPptxs = (profiles) => {
    const pptxgen = import(/* webpackChunkName: "pptxgenjs" */ "pptxgenjs");
    const fileSaver = import(/* webpackChunkName: "file-saver" */ "file-saver");
    const jszip = profiles.length > 1 ? import(/* webpackChunkName: "jszip" */ "jszip") : null;
    const localExportBuildPptx = getLocalExportBuildPptx(config.templateKey);

    Promise.all([pptxgen, fileSaver, jszip]).then(
      ([{ default: pptxgen }, { default: saveAs }, JSZipModule]) => {
        const promises = getProfilesWithAvatars(profiles, (profile, filename, profileResolve) => {
          const pptx = localExportBuildPptx(profile, config, order, pptxgen, t, formatDate);
          pptx.write({ outputType: "blob" }).then((data) => profileResolve({ data, filename }));
        });

        batchDownload(promises, ".pptx", saveAs, JSZipModule?.default);
      }
    );
  };

  const buildPdfs = (profiles) => {
    const pdfMake = import(/* webpackChunkName: "pdfmake" */ "pdfmake/build/pdfmake.js");
    const pdfFonts = import(/* webpackChunkName: "pdffonts" */ "pdfmake/build/vfs_fonts.js");
    const fileSaver = import(/* webpackChunkName: "file-saver" */ "file-saver");
    const jszip = profiles.length > 1 ? import(/* webpackChunkName: "jszip" */ "jszip") : null;

    Promise.all([pdfMake, pdfFonts, fileSaver, jszip]).then(
      ([pdfMake, pdfFonts, { default: saveAs }, JSZipModule]) => {
        //@ts-ignore
        window.pdfMake = pdfMake;
        //@ts-ignore
        window.pdfMake.vfs = pdfFonts.pdfMake.vfs;

        const promises = getProfilesWithAvatars(profiles, (profile, filename, profileResolve) => {
          const pdf = localExportBuildPdf(profile, config, order, pdfMake);
          pdf.getBlob((data) => profileResolve({ data, filename }));
        });

        batchDownload(promises, ".pdf", saveAs, JSZipModule?.default);
      }
    );
  };

  const buildDocxs = (profiles) => {
    const docxModule = import(/* webpackChunkName: "docx" */ "docx");
    const fileSaver = import(/* webpackChunkName: "file-saver" */ "file-saver");
    const jszip = profiles.length > 1 ? import(/* webpackChunkName: "jszip" */ "jszip") : null;

    Promise.all([docxModule, fileSaver, jszip]).then(([docx, { default: saveAs }, JSZipModule]) => {
      const promises = getProfilesWithAvatars(profiles, (profile, filename, profileResolve) => {
        localExportBuildDocx(profile, config, docx).then((data) =>
          profileResolve({
            data,
            filename
          })
        );
      });

      batchDownload(promises, ".docx", saveAs, JSZipModule?.default);
    });
  };

  const downloadCsv = () => {
    const [headers, items] = profilesToTable(exportableProfiles.filterEnabled(), order, {
      removeEmptyColumns: config.removeEmptyColumns,
      hidden: config.hidden,
      isBlindModeOn: config.isBlindModeOn,
      formatDate: formatDateTime
    });

    const filename =
      items.length > 1
        ? "profiles.csv"
        : config.isBlindModeOn
        ? `Candidate ${profileHash(serializedProfile?.id)}`
        : slugify(
            `${serializedProfile?.first_name}_${serializedProfile?.last_name || ""}_${serializedProfile?.id}`
          )
            .concat(".csv")
            .replace(/-+/g, "_");

    formatAndDownload(filename, headers, items);
  };

  const handleDownload = (bulk) => {
    const buildFunction = {
      pdf: buildPdfs,
      docx: buildDocxs,
      pptx: buildPptxs,
      csv: downloadCsv
    }[config.exportTemplate];

    if (bulk) {
      buildFunction(exportableProfiles.filterEnabled());
    } else {
      buildFunction([exportableProfile]);
    }
    onDownload();
  };

  return { handleDownload, downloadCsv, serializedProfile };
};

export default useDownloadProfiles;
